import React, { useRef } from 'react'

import { Typography, Button, Row, Col } from 'antd'
import { UploadProps } from 'antd/lib/upload'

import { observer } from 'mobx-react-lite'

import { FlexBoxY, Box } from 'common/components/boxes'
import DateTimePicker from 'common/components/date_time_picker'
import { DrawerRef } from 'common/components/Drawer'
import Address from 'common/components/full_address'
import { useTheme } from 'common/hooks/use-theme'
import { OrderStates, File } from 'common/server/server_types'

import { DeliveryDetail } from 'vendor/components/DeliveryDetail/delivery_detail'
import { Delivery } from 'vendor/server/deliveries'
import { DeliveryUpdateParams, Delivery as IDelivery } from 'vendor/server/deliveries'
import { Order } from 'vendor/server/orders'

export type DeliveryHeaderProps = {
  delivery: Delivery
  order: Order
  onChange: (params: { [key: string]: string | number }) => void
  addExistingFiles: (key: string, file: File[] | File) => void
  fileList: (key: string) => UploadProps['fileList']
  updateDelivery: (delivery: DeliveryUpdateParams) => Promise<IDelivery>
  disabled?: boolean
}

export const DeliveryHeader = observer<DeliveryHeaderProps>(
  ({ delivery, order, onChange, addExistingFiles, fileList, updateDelivery, disabled }) => {
    const theme = useTheme()

    const deliveryDetailRef = useRef<DrawerRef>()

    const { estimated_delivered_at } = delivery
    const orderState = order.state

    const showEstimatedDeliveredAt = orderState != OrderStates.QUOTED && orderState != OrderStates.CANCELLED

    return (
      <Box mb={12}>
        <Row gutter={[12, 8]} justify="space-between">
          <Col>
            <Row gutter={16} justify="space-between">
              <Col>
                <Box display="flex" flexDirection="column" maxWidth="300px">
                  <Typography.Text type="secondary">
                    {delivery.is_pick_up ? 'Pickup At' : 'Delivery To'}
                  </Typography.Text>
                  <Address address={delivery.address} deliveryAddressTo={delivery.address_to} showDetails />
                </Box>
              </Col>

              {delivery.instructions && (
                <Col
                  style={{
                    borderLeft: `1px solid ${theme.colors['gray-2']}`,
                    borderRight: `1px solid ${theme.colors['gray-2']}`,
                  }}
                >
                  <Box display="flex" flexDirection="column" maxWidth="300px">
                    <Typography.Text type="secondary">Instructions</Typography.Text>
                    <Typography.Text>{delivery.instructions}</Typography.Text>
                  </Box>
                </Col>
              )}

              <Col>
                <Button
                  data-cy={delivery.is_pick_up ? 'pick-up-details' : 'shipping-details'}
                  onClick={(event) => {
                    event.stopPropagation()
                    deliveryDetailRef.current?.show()
                  }}
                  disabled={disabled}
                  type="dashed"
                >
                  {delivery.is_pick_up ? 'Pick Up Details' : 'Shipping Details'}
                </Button>
              </Col>
            </Row>
          </Col>

          <DeliveryDetail
            ref={deliveryDetailRef}
            onClose={() => deliveryDetailRef.current?.close()}
            delivery={delivery}
            onUpdate={async (delivery) => {
              const newDelivery = await updateDelivery(delivery)
              onChange(newDelivery as {})
              deliveryDetailRef.current?.close()
            }}
            addExistingFiles={addExistingFiles}
            fileList={fileList}
          />

          {showEstimatedDeliveredAt && (
            <Col>
              <FlexBoxY justifyContent="flex-start" alignItems="flex-start" onClick={(e) => e.stopPropagation()}>
                <Typography.Text type="secondary">
                  {delivery.is_pick_up ? 'Ready for Pickup By' : 'Estimated Delivery'}
                </Typography.Text>
                <DateTimePicker
                  value={estimated_delivered_at}
                  onChange={(estimated_delivered_at) => onChange({ estimated_delivered_at })}
                  placeholder="Optional"
                  wrapperProps={{ width: '100%' }}
                  disabled={disabled}
                />
              </FlexBoxY>
            </Col>
          )}
        </Row>
      </Box>
    )
  },
)
